import { base64ToStr } from '@payfit/growth-platform-brand-system-v2';
import { allButtonsSelector } from '../utils/allButtonsSelector';
import { ctaList } from '../utils/ctaList';
import { DATA_TRACKING_PLACEMENT } from '../utils/getTrackingAttributes';
import { getTrackingDefaultPayload } from '../utils/getTrackingDefaultPayload';

// Dictionary to keep track of active listeners
const activeListeners = new WeakMap();

// Store observer and timeout for cleanup
let globalObserver: MutationObserver | null = null;
let globalDebounceTimeout: NodeJS.Timeout | null = null;

const getClickEventName = (event: Event) => {
  let eventName = 'click';
  if (event.currentTarget instanceof HTMLAnchorElement) {
    const target = event.currentTarget as HTMLAnchorElement;
    if (ctaList.some((cta) => target.href?.includes(cta))) {
      eventName = 'click_cta';
    }
  }
  if (
    event.currentTarget instanceof HTMLElement &&
    event.currentTarget.closest('.Faq')
  ) {
    eventName = 'click_faq';
  }
  return eventName;
};

function getClickName(event: Event) {
  const link = event.currentTarget as HTMLElement;
  // Get the text of each direct child, separated by a pipe, excluding uy-sr-only elements
  const pipeSeparatedClickName = Array.from(link.childNodes)
    .map((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.trim() || '';
      }
      if (node instanceof HTMLElement) {
        // Skip elements with uy-sr-only class
        if (node.classList.contains('uy-sr-only')) {
          return '';
        }
        return node.innerText.trim();
      }
      return '';
    })
    .filter(Boolean)
    .join(' | ');

  // If pipeSeparatedText is empty, search the src of an image or the href of a <use> tag
  let clickName: string | null = pipeSeparatedClickName;

  /** Search clickName in Img or sprite svg */
  if (!clickName) {
    const img = link.querySelector('img:not(.uy-sr-only)');
    if (img && img instanceof HTMLImageElement && img.src) {
      clickName = img.src;
    } else {
      const use = link.querySelector('use:not(.uy-sr-only)');
      if (use && use instanceof SVGUseElement) {
        clickName = use.getAttribute('href');
      }
    }
  }

  /** Search clickName in aria-label */
  if (!clickName) {
    clickName = link.getAttribute('aria-label');
  }

  /** Not Find Name */
  if (!clickName) {
    clickName = 'unknown';
  }
  return clickName;
}

function normalizeClickUrl(clickUrl: string) {
  // Remove protocol
  const cleanHref = clickUrl.replace(/^https?:\/\//, '');

  if (cleanHref.includes(window.location.host)) {
    // remove domain and keep path
    // Ex: website.dev.payfit.tech/github_branch/ => /github_branch/
    const firstSlash = cleanHref.indexOf('/');
    if (firstSlash !== -1) {
      const path = cleanHref.slice(firstSlash); // path included
      return `${window.location.host}${path}`;
    }
    return `${window.location.host}/`;
  }

  // Cas path absolu
  if (cleanHref.startsWith('/')) {
    return `payfit.com${cleanHref}`;
  }

  return cleanHref;
}

function getUrl(event: Event) {
  let href: string | undefined;
  const target = event.currentTarget;
  if (target instanceof HTMLAnchorElement) {
    href = target.href;
  }
  if (!href && target instanceof HTMLElement) {
    const qcd = target.getAttribute('data-qcd');
    if (qcd) {
      href = base64ToStr(qcd);
    }
  }
  return href;
}

function getEventClickType(event: Event) {
  const target = event.currentTarget;
  const isFaq = target instanceof HTMLElement && target.closest('.Faq');

  if (isFaq) {
    return 'faq';
  }

  const href = getUrl(event);
  if (href) {
    const normalizedHref = normalizeClickUrl(href);
    if (ctaList.some((cta) => normalizedHref.startsWith(cta))) {
      return 'click_cta';
    }
    if (!href.includes(window.location.host)) {
      return 'outbound';
    }
  }
  return 'interaction';
}

function getClickPlacement(event: Event) {
  const target = event.currentTarget as HTMLElement;
  let parentPlacement = target.closest(`[${DATA_TRACKING_PLACEMENT}]`);
  if (parentPlacement) {
    return parentPlacement.getAttribute(DATA_TRACKING_PLACEMENT);
  }
  parentPlacement = target.closest('footer');
  if (parentPlacement) {
    return 'footer';
  }
  return 'body';
}

function handleClick(event: Event) {
  if (!window.analytics || typeof window.analytics?.track !== 'function') {
    console.warn('Analytics not initialized');
    return;
  }
  if (
    event.currentTarget instanceof HTMLElement &&
    event.currentTarget.getAttribute('data-tracking-click-disabled') === 'true'
  ) {
    return;
  }

  const { title, ...rest } = getTrackingDefaultPayload();

  try {
    window.analytics?.track(getClickEventName(event), {
      ...rest,
      clickName: getClickName(event),
      clickType: getEventClickType(event),
      clickPage: title,
      clickPlacement: getClickPlacement(event),
      clickUrl: getUrl(event),
      target: (event.currentTarget as HTMLElement)?.classList,
    });
  } catch (error) {
    console.error('Failed to track click event:', {
      error,
      eventName: getClickEventName(event),
      clickName: getClickName(event),
      clickType: getEventClickType(event),
    });
  }
}

function attachClickEvent(elt: Node) {
  if (!activeListeners.has(elt)) {
    const clickListener = (event: Event) => {
      handleClick(event);
    };
    elt.addEventListener('click', clickListener);
    activeListeners.set(elt, clickListener);
  }
}

function attachClickEvents(elts: Node[]) {
  elts.forEach((elt) => {
    attachClickEvent(elt);
  });
}

// Function to clean up removed listeners
function cleanUpRemovedNodeListeners(mutations: MutationRecord[]) {
  mutations.forEach((mutation) => {
    mutation.removedNodes.forEach((node) => {
      // Check if the removed node is an anchor element and has an active listener
      if (node instanceof HTMLElement && activeListeners.has(node)) {
        // Remove the click event listener from the link
        node.removeEventListener('click', activeListeners.get(node));
        // Delete the listener from the WeakMap
        activeListeners.delete(node);
      }
    });
  });
}

function observeAllCLicks() {
  let isProcessing = false;

  // Observer to monitor the addition and removal of links
  globalObserver = new MutationObserver((mutations) => {
    if (isProcessing) return;
    isProcessing = true;

    // Debounce the mutations to avoid multiple rapid calls
    if (globalDebounceTimeout) {
      clearTimeout(globalDebounceTimeout);
    }
    globalDebounceTimeout = setTimeout(() => {
      const newElements = mutations.flatMap((mutation) =>
        Array.from(mutation.addedNodes).filter(
          (node) =>
            node instanceof HTMLElement && node.matches(allButtonsSelector),
        ),
      );

      if (newElements) {
        attachClickEvents(newElements);
      }
      cleanUpRemovedNodeListeners(mutations);
      isProcessing = false;
    }, 100);
  });

  // Start observing the document body for child node changes
  globalObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// Export the main function to start tracking
export function clickEvent() {
  const elts = document.querySelectorAll(allButtonsSelector);
  attachClickEvents(Array.from(elts));
  observeAllCLicks();

  // Return a destroy function to clean up all event listeners
  return function destroy() {
    // Clean up all active listeners
    document.querySelectorAll(allButtonsSelector).forEach((element) => {
      if (element instanceof HTMLElement && activeListeners.has(element)) {
        element.removeEventListener('click', activeListeners.get(element));
        activeListeners.delete(element);
      }
    });

    // Disconnect the observer
    if (globalObserver) {
      globalObserver.disconnect();
      globalObserver = null;
    }

    // Clear the timeout
    if (globalDebounceTimeout) {
      clearTimeout(globalDebounceTimeout);
      globalDebounceTimeout = null;
    }
  };
}
